import itineraryApi from '@/src/api/rtkQuery/itineraryApi'
import useGetItineraryId from '@/src/hooks/itinerary/useGetItineraryId'
import usePriceCalculation from '@/src/hooks/itinerary/usePriceCalculation'
import { RootState } from '@/src/redux/rootReducer'
import { useAppSelector } from '@/src/redux/store'

import { extractFlightInfo } from '@/src/utils/analytics/clevertap/itinerary'
import { getDeviceType } from '@/src/utils/api/getDeviceType'
import { getUserAuthValues } from '@/src/utils/auth'
import { getFormattedDate } from '@/src/utils/date'
import { getPlatformName } from '@/src/utils/deviceType'
import { isUserSignedIn } from '@/src/utils/generalUtils/checkUserLogin'
import { useLocale, useTranslations } from 'next-intl'
import { useMemo } from 'react'

const useItineraryGtmEvent = () => {
    const locale = useLocale()
    const t = useTranslations('metadata')
    const itineraryId = useGetItineraryId()
    const isSignedIn = isUserSignedIn()
    const deviceType = getDeviceType()
    const platform = getPlatformName(deviceType)
    const data = useAppSelector((state: RootState) => {
        const cachedData = itineraryApi.endpoints.getItineraryData.select({
            itineraryId
        })(state)
        return cachedData?.status === 'fulfilled' ? cachedData.data : null
    })
    const { email } = getUserAuthValues() || {}
    const { totalPrice } = usePriceCalculation()
    const userEmailPhone = isSignedIn ? `{"user_email":"${email || 'not_informed'}","user_phone":"not_informed"}` : null

    // Destructure main data objects
    const { flights = [], searchCriteria, itineraryData } = data || {}
    const { currencyConversionFactor } = itineraryData || {}
    const { paxInfo, departDate, arrivalDate } = searchCriteria || {}
    const { adults = 0, children = 0, infants = 0 } = paxInfo || {}

    const { airlineName, flightIds, cabinType, flightRouteStops } = useMemo(
        () => extractFlightInfo(flights, '|'),
        [flights]
    )
    const origin = flights[0]?.from?.toLowerCase() || ''
    const fromCity = flights[0]?.fromCity?.toLowerCase() || ''
    const destination = flights[0]?.to?.toLowerCase() || ''
    const toCity = flights[0]?.toCity?.toLowerCase() || ''
    const tripType = arrivalDate ? 'round_trip' : 'one_way_trip'
    const departureDate = departDate?.split('T')[0] || ''
    const returnDate = arrivalDate?.split('T')[0] || ''
    const item = `${origin}-${destination}-${arrivalDate ? 'rt' : 'ow'}`

    const pushEventToDataLayer = (payload = {}) => {
        // TODO: When writing for Cleartrip, we can define a function here that creates the payload.
        // Based on it, for Cleartrip or Flyin we can have two functions and we will use that to get the payload.

        const finalPayload = {
            item,
            PageType: 'FlightReviewPage',
            event: 'page_view_flight_itinerary_details',
            trip_type: tripType,
            source_airport_code: origin,
            destination_airport_code: destination,
            source_airport_city: fromCity,
            destination_airport_city: toCity,
            travelers_adult_count: adults,
            travelers_children_count: children,
            travelers_infants_count: infants,
            travelers_total_count: adults + children + infants,
            departing_departure_date: getFormattedDate(departureDate, 'YYYY/MM/DD'),
            returning_departure_date: returnDate
                ? getFormattedDate(returnDate, 'YYYY/MM/DD')
                : 'no_returning_departure_date',
            flight_class: cabinType,
            airline_names: airlineName,
            flight_ids: flightIds,
            flight_route_stops: flightRouteStops,
            booking_flight_total_cost: totalPrice,
            page_category: 'search_flight',
            product_category: 'flights',
            page_referrer: document.referrer || 'no_page_referrer',
            page_language: locale,
            page_url: window.location.href,
            page_sub_category: 'flights',
            page_path: window.location.pathname,
            page_domain: window.location.hostname,
            page_title: t('title'),
            page_country: window.location.hostname === 'www.flyin.com' ? 'global' : 'eg',
            page_currency: currencyConversionFactor?.code?.toLowerCase() || '',
            user_email_phone: userEmailPhone,
            user_logged_in: isSignedIn ? 'yes' : 'no',
            is_new_flow: 'yes',
            platform,
            ...payload
        }

        if (typeof window !== 'undefined' && window.dataLayer && Array.isArray(window.dataLayer)) {
            window.dataLayer.push(finalPayload)
        }
    }

    return {
        pushEventToDataLayer
    }
}

export default useItineraryGtmEvent
